/**
 * TodayStep
 *
 * A single step in the Today's Card 5-step timeline.
 *
 * Visual states:
 *  - locked   : gray circle + lock icon, title dimmed, no content
 *  - unlocked : blue circle + step number, expanded by default, content visible
 *  - done     : green circle + ✓, collapsed by default with a compact summary,
 *               tap the header to re-expand and see the done-state card (with undo)
 *
 * The `children` prop is always the expandable body. Pass the full card form
 * when undone *or* the done-state card when done — the caller controls content.
 *
 * Usage:
 *  <TodayStep num={1} title="Weight" done={weightDone} unlocked={true} summary="68.4 kg">
 *    {weightDone ? <WeightDoneCard /> : <WeightForm />}
 *  </TodayStep>
 */

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Lock, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';

// ── StepHint ─────────────────────────────────────────────────────────────────
function StepHint({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex items-center">
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        className="p-0.5 rounded-full text-gray-400 hover:text-primary-brown transition-colors shrink-0"
        aria-label="Hint"
      >
        <Info size={14} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-6 z-30 bg-white text-text-secondary text-xs rounded-xl px-3 py-2 shadow-md leading-snug whitespace-nowrap">
          {text}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 shadow-[-1px_-1px_2px_rgba(0,0,0,0.06)]" />
        </div>
      )}
    </div>
  );
}

interface TodayStepProps {
  /** 1-based step number shown inside the timeline node */
  num: number;
  title: string;
  done: boolean;
  unlocked: boolean;
  /** Icon element rendered in a small coloured badge before the title */
  icon?: React.ReactNode;
  /** Tailwind bg class or inline hex for the icon badge, e.g. 'bg-rose-500' */
  iconColor?: string;
  /** One-line text shown below the title when done and collapsed */
  summary?: string;
  /** Whether this is the last step (omits the connector line below) */
  isLast?: boolean;
  /** The first unlocked+incomplete step — scrolls into view on mount */
  isNext?: boolean;
  /** Optional hint text shown in a small popover next to the title via an info icon */
  hint?: string;
  children: React.ReactNode;
}

export function TodayStep({
  num,
  title,
  done,
  unlocked,
  icon,
  iconColor,
  summary,
  isLast = false,
  isNext = false,
  hint,
  children,
}: TodayStepProps) {
  // Only the designated next step starts open; all others start collapsed.
  const [open, setOpen] = useState(() => isNext && !done);
  const { t } = useLanguage();

  // When a step transitions to done, collapse it automatically
  useEffect(() => {
    if (done) setOpen(false);
  }, [done]);

  // When a step first becomes unlocked mid-session (e.g. weight saved → AM vitals
  // unlocks), expand it automatically — but only if it is now the next step.
  useEffect(() => {
    if (isNext && unlocked && !done) setOpen(true);
  }, [isNext, unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const canToggle = unlocked;

  // Scroll the step to just below the fixed tab bar when it opens.
  // On mount we only scroll for the designated 'isNext' step so multiple
  // steps starting open don't fight each other. After mount, every
  // user-triggered open scrolls normally.
  const stepRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      // Only scroll on mount if this is explicitly the next step
      if (isNext && stepRef.current) {
        stepRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    // Post-mount: scroll whenever the step opens (user tap or auto-unlock)
    if (open && stepRef.current) {
      stepRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [open, isNext]);
  // ── Node styles ──────────────────────────────────────────────────────────
  const nodeBase = 'w-8 h-8 rounded-full flex items-center justify-center shrink-0';
  // When unlocked, apply iconColor as a className (e.g. 'bg-rose-500')
  // so each step circle matches its section colour.
  const nodeStyle = done
    ? cn(nodeBase, 'bg-positive-green text-white')
    : unlocked
    ? cn(nodeBase, iconColor ?? 'bg-primary-brown', 'text-white')
    : cn(nodeBase, 'bg-gray-200 text-gray-400');

  const connectorStyle = cn(
    'w-0.5 flex-1 mt-1 mb-0',
    done ? 'bg-positive-green/30' : unlocked && iconColor ? iconColor + '/30' : unlocked ? 'bg-primary-brown/20' : 'bg-gray-200',
  );

  // ── Title styles ─────────────────────────────────────────────────────────
  const titleStyle = cn(
    'font-bold text-base leading-tight',
    done ? 'text-positive-green' : unlocked ? 'text-text-primary' : 'text-gray-400',
  );

  return (
    <div ref={stepRef} className="flex gap-3" style={{ scrollMarginTop: 70 }}>
      {/* Left — timeline node + connector */}
      <div className="flex flex-col items-center" style={{ width: 32 }}>
        <div className={nodeStyle}>
          {done ? (
            <CheckCircle size={16} strokeWidth={2.5} />
          ) : unlocked ? (
            // Clone the icon at a fixed size for the node
            icon ? <span className="[&>svg]:w-4 [&>svg]:h-4">{icon}</span> : <span className="text-sm font-bold">{num}</span>
          ) : (
            <Lock size={14} strokeWidth={2} />
          )}
        </div>
        {!isLast && <div className={connectorStyle} style={{ minHeight: 24 }} />}
      </div>

      {/* Right — header + collapsible body */}
      <div className="flex-1 pb-5">
        {/* Header row — uses div instead of button to avoid nested <button> (StepHint) */}
        <div
          role={canToggle ? 'button' : undefined}
          tabIndex={canToggle ? 0 : undefined}
          onClick={() => canToggle && setOpen(o => !o)}
          onKeyDown={e => { if (canToggle && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(o => !o); } }}
          className={cn(
            'w-full flex items-center justify-between pt-0.5 pb-2 text-left gap-2',
            !canToggle ? 'cursor-default' : 'cursor-pointer',
          )}
          aria-expanded={canToggle ? open : undefined}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={titleStyle}>{title}</span>
                {hint && <StepHint text={hint} />}
              </div>
              {/* Compact summary shown when done + collapsed */}
              {done && !open && summary && (
                <span className="text-xs text-text-secondary mt-0.5 truncate">{summary}</span>
              )}
            </div>
          </div>
          {canToggle && (
            <span className="text-gray-400 shrink-0">
              {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </span>
          )}
          {!unlocked && (
            <span className="text-xs text-gray-400 shrink-0">{t('records.stepLocked')}</span>
          )}
        </div>

        {/* Body — only visible when open and unlocked */}
        {open && unlocked && (
          <div>{children}</div>
        )}
      </div>
    </div>
  );
}
